import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { COURAGES, LIEUX } from '@nextilt/commun';
import { api } from '../partage/api/client';
import { cles } from '../partage/relectures';
import { possibles } from './reglagesDuPouce';
import { Curseur, LigneQuiOuvre, ListeDeChoix } from '../partage/PiecesDuCarnet';
import type { Pastille } from './ChoixDuDossier';
import { requeteDesReglages, type ReglagesDeMaintenant } from './reglagesDeMaintenant';
import {
  cransDuTemps,
  MOTS_COURAGE,
  MOTS_LIEU,
  N_IMPORTE_OU,
  REGLAGE_COURAGE,
  REGLAGE_LIEU,
  REGLAGE_PROJET,
  REGLAGE_TEMPS,
  TOUS,
  TOUT_LE_TEMPS,
  voirLesPossibles,
} from '../partage/vocabulaire';
import './PanneauDesReglages.css';

/**
 * Les réglages de « Maintenant » au téléphone (chantier `telephone-cadre`, ticket 03 ; décision
 * d'Igor du 2026-10-05, revue 5) : J'ai, Je suis, Courage, puis le projet (l'ordre de la
 * maquette du carnet), avec les choix de l'ordinateur. Depuis le chantier telephone-habit (02), ils vivent dans la feuille du ⚙ de
 * l'écran (`HautDuPouce`), qui les porte. Tout se choisit d'un coup, rien ne s'applique avant
 * « Voir ».
 *
 * Le bouton dit combien de tâches répondent aux réglages du panneau (« Voir les 3 possibles ») :
 * un appel à `/api/maintenant` avec eux, sans les appliquer ; le serveur compte (ses pastilles).
 * « Voir » applique et ferme la feuille ; le voile ou Échap la ferment sans rien changer.
 */
export function PanneauDesReglages({
  reglages,
  projet,
  pastilles,
  onVoir,
}: {
  reglages: ReglagesDeMaintenant;
  projet: Pastille | null;
  /** Les projets qu'on peut choisir : ceux de la réponse en cours. */
  pastilles: Pastille[];
  onVoir: (reglages: ReglagesDeMaintenant, projet: Pastille | null) => void;
}) {
  const [choix, setChoix] = useState<ReglagesDeMaintenant>(reglages);
  const [dossier, setDossier] = useState<Pastille | null>(projet);
  const changer = (r: Partial<ReglagesDeMaintenant>) => setChoix((c) => ({ ...c, ...r }));
  // La liste ouverte sous sa ligne : le projet ou « J'ai », une à la fois.
  const [liste, setListe] = useState<'projet' | 'temps' | null>(null);

  // La même requête que la page : « Voir » trouve la réponse déjà là.
  const requete = requeteDesReglages(choix, dossier?.id ?? null, false);
  const apercu = useQuery({
    queryKey: cles.maintenant(requete),
    queryFn: () => api.maintenant(requete),
    placeholderData: keepPreviousData,
  });
  const combien = apercu.data && !apercu.isPlaceholderData ? possibles(apercu.data.pastilles, dossier?.id ?? null) : null;

  // Le projet choisi reste dans la liste, même quand rien n'y passe avec les réglages d'avant.
  const projets = dossier && !pastilles.some((p) => p.id === dossier.id) ? [...pastilles, dossier] : pastilles;
  const temps = [[null, TOUT_LE_TEMPS] as const, ...cransDuTemps()];
  const motDuTemps = temps.find(([t]) => t === choix.temps)?.[1] ?? TOUT_LE_TEMPS;

  /*
   * Plus de pastilles (chantier telephone-habit, 03) : le projet et « J'ai » (six choix) sont des
   * choix longs, une ligne qui ouvre sa liste ; « Je suis » et le courage, trois positions, des
   * curseurs à crans ; « Voir » le bouton foncé des gestes.
   */
  return (
    <div className="panneau-reglages-maintenant">
      <div data-tuto="reglage-temps">
        <LigneQuiOuvre
          titre={REGLAGE_TEMPS}
          valeur={motDuTemps}
          ouverte={liste === 'temps'}
          onOuvrir={() => setListe(liste === 'temps' ? null : 'temps')}
          attributs={{ 'data-reglage': 'temps' }}
        />
        {liste === 'temps' && (
          <ListeDeChoix
            nom={REGLAGE_TEMPS}
            choix={temps}
            choisi={choix.temps}
            attribut="temps"
            onChoisir={(t) => {
              changer({ temps: t });
              setListe(null);
            }}
          />
        )}
      </div>

      <Curseur
        titre={REGLAGE_LIEU}
        crans={[[null, N_IMPORTE_OU] as const, ...LIEUX.map((l) => [l, MOTS_LIEU[l]] as const)]}
        choisi={choix.lieu}
        attribut="lieu"
        onChoisir={(l) => changer({ lieu: l })}
      />

      <Curseur
        titre={REGLAGE_COURAGE}
        crans={COURAGES.map((c) => [c, MOTS_COURAGE[c]] as const)}
        choisi={choix.courage}
        attribut="courage"
        onChoisir={(c) => changer({ courage: c })}
      />

      <LigneQuiOuvre
        titre={REGLAGE_PROJET}
        valeur={dossier ? dossier.titre : TOUS}
        ouverte={liste === 'projet'}
        onOuvrir={() => setListe(liste === 'projet' ? null : 'projet')}
        attributs={{ 'data-reglage': 'projet' }}
      />
      {liste === 'projet' && (
        <ListeDeChoix
          nom={REGLAGE_PROJET}
          choix={[[null, TOUS] as const, ...projets.map((p) => [p.id, p.titre] as const)]}
          choisi={dossier?.id ?? null}
          attribut="dossier"
          onChoisir={(id) => {
            setDossier(id === null ? null : (projets.find((p) => p.id === id) ?? null));
            setListe(null);
          }}
        />
      )}

      <div className="pouce-acts">
        <button type="button" className="panneau-reglages-voir est-principal" onClick={() => onVoir(choix, dossier)}>
          {voirLesPossibles(combien)}
        </button>
      </div>
    </div>
  );
}
